import { useEffect } from 'react';
import WaveSurfer from 'wavesurfer.js';
import type { AppConfig } from '@/lib/types';

import { ScriptMarker } from './ScriptMarker';
import type { useTimeline } from './useTimeline';

interface TimelineProps {
  config: AppConfig;
  onChange: (config: AppConfig) => void;
  timeline: ReturnType<typeof useTimeline>;
}

export function Timeline({ config, onChange, timeline: t }: TimelineProps) {

  useEffect(() => {
    if (!t.musicContainerRef.current || !t.voContainerRef.current) return;

    const commonOptions = {
      height: 80,
      waveColor: '#4b5563',
      progressColor: '#d97706',
      cursorColor: 'transparent',
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      normalize: true,
      fillParent: true,
      mediaControls: false,
      interact: false,
      hideScrollbar: true,
    };

    const ms = WaveSurfer.create({
      ...commonOptions,
      container: t.musicContainerRef.current,
      waveColor: '#2563eb22',
      progressColor: '#2563eb',
    });
    t.musicInstanceRef.current = ms;

    const vs = WaveSurfer.create({
      ...commonOptions,
      container: t.voContainerRef.current,
      waveColor: '#f59e0b22',
      progressColor: '#f59e0b',
    });
    t.voInstanceRef.current = vs;

    // Removed local timeupdate listeners. Remotion's player is the sole source of truth.
    // The playhead will now smoothly track Remotion's time via onFrameUpdate,
    // avoiding jitter when seeking past the end of shorter audio tracks.

    const loadAudio = async () => {
      if (config.audio.musicUrl) {
        await ms.load(config.audio.musicUrl).catch(() => {});
      }
      if (config.audio.voiceoverUrl) {
        await vs.load(config.audio.voiceoverUrl).catch(() => {});
      }
    };

    loadAudio();

    return () => {
      ms.destroy();
      vs.destroy();
      t.musicInstanceRef.current = null;
      t.voInstanceRef.current = null;
    };
  }, [config.audio.musicUrl, config.audio.voiceoverUrl]);

  return (
    <div className="flex flex-col flex-1 min-h-0 pb-10">
      <ScriptMarker 
        config={config}
        currentTime={t.currentTime}
        onAddSegment={t.addSegmentWithLines}
        onDeleteSegment={t.deleteSegment}
      />
    </div>
  );
}
